/**
 * ProjectsGrid - Column definitions and grid configuration for the Projects table.
 *
 * Architecture (following EBTimeEntry's TimesheetReportGrid pattern):
 *   ProjectsPage (data fetching, filters, pagination state)
 *     └─ ProjectsGrid (column definitions, cell renderers)  ← this file
 *         └─ DataTableEnhanced (generic table rendering)
 *
 * All cell rendering logic that was previously in the monolithic renderColumnValue
 * switch statement in projects.tsx is now expressed as TanStack ColumnDef cell renderers.
 */

import React, { useMemo, useCallback } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle } from 'lucide-react';
import { SensitiveData } from '@/components/sensitive-data';
import {
  DataTableEnhanced,
  columnDefinitionsToVisibility,
  type ColumnSort,
} from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  healthToVariant,
  formatStatus,
  getVarianceColor,
  calculateDaysRemaining,
  calculateForecastAtCompletion,
  formatProjectType,
  formatRelativeTime,
  getDaysRemainingColor,
  getProjectTypeColor,
  getWIPColor,
} from '@/lib/utils';
import { getProjectStatusColor } from '@/lib/status-colors';
import type { Project, Client, Employee, Task, Milestone, ResourceAllocation } from '@/types';
import { PROJECT_STATUS_LABELS } from '@/types';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';
import type { PaginationMetadata } from '@/types/pagination';
import { calculateGP } from '@/lib/gp-calculation';
import { computeEffectiveCompletionPercent } from '@/lib/completion';
import { getFinanceSettings } from '@/lib/finance-settings-storage';
import { getBudgetHealthThresholds, deriveProjectHealthWithBreakdown } from '@/lib/budget-health-thresholds-storage';
import { getUtilisationSettings, computeUtilisationRate } from '@/lib/utilisation-settings-storage';

// ── Types ────────────────────────────────────────────────────────────────

/** Reference data needed by cell renderers */
export interface ProjectsGridReferenceData {
  clients: Client[];
  employees: Employee[];
  tasks: Task[];
  milestones: Milestone[];
  resourceAllocations: ResourceAllocation[];
}

export interface ProjectsGridProps {
  /** Pre-sorted/paginated project data to render */
  data: Project[];
  /** Reference data for lookups in cell renderers */
  referenceData: ProjectsGridReferenceData;
  /** Column definitions from ColumnConfiguratorDrawer (controls visibility & order) */
  columnDefinitions: ColumnDefinition[];
  /** Multi-column sort state */
  columnSorts: ColumnSort[];
  /** Callback when sort state changes */
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  /** Callback when a project row is clicked */
  onRowClick: (project: Project) => void;
  /** Show loading skeleton */
  loading?: boolean;
  /** Show transition overlay */
  isTransitioning?: boolean;
  /** Number of skeleton rows when loading */
  pageSize?: number;
  /** Current search/filter state for empty message */
  hasActiveFilters?: boolean;
  /** Pagination metadata for pagination controls */
  pagination?: PaginationMetadata;
  /** Callback for page changes */
  onPageChange?: (page: number) => void;
  /** Callback for page size changes */
  onPageSizeChange?: (size: number) => void;
  /** When true, suppress the column header row (used inside grouped view) */
  hideHeader?: boolean;
  /** Optional subtotal row rendered after the last data row (used inside grouped view) */
  subtotalRow?: React.ReactNode;
  // ── Row Selection ──────────────────────────────────────────────────
  /** Set of selected project IDs (controlled externally) */
  selectedIds?: Set<number>;
  /** Called when the selection changes */
  onSelectionChange?: (ids: Set<number>) => void;
}

// ── Helper functions (extracted from projects.tsx) ───────────────────────

function getClientNames(project: Project, clients: Client[]): string {
  if (project.clientNames) return project.clientNames;
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const names = ids.map(
      (id) => clients.find((c) => c.id === parseInt(id, 10))?.name || 'Unknown',
    );
    return names.join(', ');
  }
  return 'Unknown';
}

function getClientCodes(project: Project, clients: Client[]): string {
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const codes = ids.map(
      (id) => clients.find((c) => c.id === parseInt(id, 10))?.code || '-',
    );
    return codes.join(', ');
  }
  return '-';
}

function getManagerName(managerId: number, employees: Employee[]): string {
  return employees.find((e) => e.id === managerId)?.name || 'Unknown';
}

function getClientContacts(project: Project, clients: Client[]): string {
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const contacts = ids
      .map((id) => clients.find((c) => c.id === parseInt(id, 10))?.contactName)
      .filter(Boolean);
    return contacts.length > 0 ? contacts.join(', ') : '-';
  }
  return '-';
}

function getOverdueTasks(projectId: number, tasks: Task[]): number {
  const now = new Date();
  return tasks.filter(
    (t) =>
      t.projectId === projectId &&
      t.status !== 'completed' &&
      new Date(t.dueDate) < now,
  ).length;
}

function getOverdueMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter(
    (m) => m.projectId === projectId && m.status === 'overdue',
  ).length;
}

function getNextMilestone(
  projectId: number,
  milestones: Milestone[],
): Milestone | undefined {
  const upcoming = milestones
    .filter((m) => m.projectId === projectId && m.status === 'pending')
    .sort(
      (a, b) =>
        new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
    );
  return upcoming[0];
}

function getAllocatedResources(
  projectId: number,
  allocations: ResourceAllocation[],
): number {
  const uniqueEmployees = new Set(
    allocations
      .filter((a) => a.projectId === projectId)
      .map((a) => a.employeeId),
  );
  return uniqueEmployees.size;
}


function getCompletedMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter(
    (m) => m.projectId === projectId && m.status === 'completed',
  ).length;
}

function getTotalMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter((m) => m.projectId === projectId).length;
}

function getUtilisationRate(project: Project): number {
  const { formulaType } = getUtilisationSettings();
  return computeUtilisationRate(project, formulaType);
}

function calculateDuration(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Derive a project phase from its statusId and completionPercent.
 * Maps the lifecycle status to a workflow phase label.
 */
function deriveProjectPhase(statusId: number, completionPercent: number): string {
  switch (statusId) {
    case 7: return 'Opportunity';
    case 1: return 'Initiation';
    case 2:
    case 4: // Reopened
      if (completionPercent <= 25) return 'Planning';
      if (completionPercent <= 75) return 'Execution';
      return 'Closing';
    case 3: return 'On Hold';
    case 5: return 'Cancelled';
    case 6: return 'Closed';
    default: return 'Unknown';
  }
}

const PHASE_COLORS: Record<string, string> = {
  'Opportunity': 'bg-accent-blue-violet-50 text-accent-blue-violet-900 border-accent-blue-violet-200',
  'Initiation':  'bg-info-50 text-info-700 border-info-200',
  'Planning':    'bg-info-50 text-info-600 border-info-200',
  'Execution':   'bg-success-50 text-success-700 border-success-200',
  'Closing':     'bg-warning-50 text-warning-700 border-warning-200',
  'Closed':      'bg-neutral-50 text-neutral-700 border-neutral-200',
  'On Hold':     'bg-accent-orange-50 text-accent-orange-900 border-accent-orange-200',
  'Cancelled':   'bg-error-50 text-error-700 border-error-200',
};

/**
 * Derive a billing status label from the project's financial position.
 * Uses fields already loaded on the project record (no extra API call).
 */
function deriveBillingStatus(project: Project): string {
  if (project.statusId === 5) return 'Cancelled';
  if (project.statusId === 7) return 'Not Started';

  const hasCosts = (project.actualCosts ?? 0) > 0;
  const hasSales = (project.actualSales ?? 0) > 0;
  const unbilled = project.unbilledAmount ?? 0;

  if (!hasCosts && !hasSales) return 'Not Started';
  if (!hasSales && hasCosts) return 'Unbilled';
  if (project.statusId === 6 && unbilled <= 0) return 'Fully Billed';
  if ((project.wipAmount ?? 0) < 0) return 'Over-billed';
  if (hasSales && unbilled > 0) return 'Partially Billed';
  return 'In Progress';
}

const BILLING_STATUS_COLORS: Record<string, string> = {
  'Not Started':      'bg-neutral-50 text-neutral-700 border-neutral-200',
  'Unbilled':         'bg-warning-50 text-warning-700 border-warning-200',
  'Partially Billed': 'bg-info-50 text-info-700 border-info-200',
  'Fully Billed':     'bg-success-50 text-success-700 border-success-200',
  'Over-billed':      'bg-error-50 text-error-700 border-error-200',
  'In Progress':      'bg-info-50 text-info-600 border-info-200',
  'Cancelled':        'bg-error-50 text-error-700 border-error-200',
};

/**
 * Derive risk level from the multi-dimensional health breakdown.
 * Counts red/amber dimensions to determine overall risk.
 */
function deriveRiskLevel(project: Project): 'High' | 'Medium' | 'Low' | 'None' {
  const thresholds = getBudgetHealthThresholds();
  const breakdown = deriveProjectHealthWithBreakdown(
    {
      actualCost: project.actualCosts ?? 0,
      budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
      startDate: project.startDate,
      endDate: project.endDate,
      completionPercent: project.completionPercent ?? 0,
      actualHours: project.actualHours ?? 0,
      budgetHours: project.budgetHours ?? 0,
      wipAmount: project.wipAmount ?? 0,
      actualRevenue: project.actualRevenue ?? 0,
      budgetRevenue: project.budgetRevenue ?? 0,
      actualGrossMarginPct: null,
      realisationRatePct: null,
      forecastEndDate: project.forecastEndDate,
    },
    thresholds,
  );
  const redCount = breakdown.dimensions.filter(d => d.signal === 'red').length;
  const amberCount = breakdown.dimensions.filter(d => d.signal === 'amber').length;

  if (redCount >= 2) return 'High';
  if (redCount >= 1 || amberCount >= 2) return 'Medium';
  if (amberCount >= 1) return 'Low';
  return 'None';
}

const RISK_LEVEL_COLORS: Record<string, string> = {
  'High':   'bg-error-50 text-error-700 border-error-200',
  'Medium': 'bg-warning-50 text-warning-700 border-warning-200',
  'Low':    'bg-warning-50 text-warning-600 border-warning-100',
  'None':   'bg-success-50 text-success-700 border-success-200',
};

/**
 * Derive a quality score (0–100) from the health breakdown dimensions.
 * Green = 100 pts, Amber = 50 pts, Red = 0 pts.
 * Returns null when no dimensions are evaluated.
 */
function deriveQualityScore(project: Project): number | null {
  const thresholds = getBudgetHealthThresholds();
  const breakdown = deriveProjectHealthWithBreakdown(
    {
      actualCost: project.actualCosts ?? 0,
      budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
      startDate: project.startDate,
      endDate: project.endDate,
      completionPercent: project.completionPercent ?? 0,
      actualHours: project.actualHours ?? 0,
      budgetHours: project.budgetHours ?? 0,
      wipAmount: project.wipAmount ?? 0,
      actualRevenue: project.actualRevenue ?? 0,
      budgetRevenue: project.budgetRevenue ?? 0,
      actualGrossMarginPct: null,
      realisationRatePct: null,
      forecastEndDate: project.forecastEndDate,
    },
    thresholds,
  );
  if (breakdown.dimensions.length === 0) return null;

  const points = breakdown.dimensions.reduce((sum, d) => {
    if (d.signal === 'green') return sum + 100;
    if (d.signal === 'amber') return sum + 50;
    return sum;
  }, 0);
  return Math.round(points / breakdown.dimensions.length);
}

function getQualityScoreColor(score: number): string {
  if (score >= 80) return 'text-success-600';
  if (score >= 50) return 'text-warning-600';
  return 'text-error-600';
}

// ── Column builder ───────────────────────────────────────────────────────

/**
 * Build the full set of TanStack column definitions for the Projects grid.
 * Each column ID matches the ColumnDefinition IDs from column-configurator-drawer.
 * Cell renderers use the provided reference data for lookups.
 */
function buildProjectColumns(
  ref: ProjectsGridReferenceData,
  statusLabels: Record<number, string>,
): ColumnDef<Project, unknown>[] {
  return [
    {
      id: 'project-name',
      accessorFn: (row) => row.name,
      header: 'Project Name',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.name}</div>
      ),
    },
    {
      id: 'project-code',
      accessorFn: (row) => row.code,
      header: 'Project Code',
      cell: ({ row }) => row.original.code || '-',
    },
    {
      id: 'client-code',
      accessorFn: (row) => getClientCodes(row, ref.clients),
      header: 'Client Code',
      cell: ({ row }) => getClientCodes(row.original, ref.clients),
    },
    {
      id: 'client-name',
      accessorFn: (row) => getClientNames(row, ref.clients),
      header: 'Client Name',
      cell: ({ row }) => {
        const project = row.original;
        if (!project.clientIds) return <span>-</span>;
        const ids = project.clientIds.split(',').filter(Boolean);
        return (
          <div className="flex flex-col gap-0.5">
            {ids.map((id) => {
              const client = ref.clients.find((c) => c.id === parseInt(id, 10));
              if (!client) return null;
              const isOnStop = client.accountStatus === 'on-stop' || client.accountStatus === 'suspended';
              return (
                <div key={id} className="flex items-center gap-1.5">
                  <span className="font-medium">{client.name}</span>
                  {isOnStop && (
                    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-warning-700 bg-warning-100 border border-warning-300 rounded px-1 py-0 leading-4 shrink-0">
                      <AlertTriangle className="h-2.5 w-2.5" />
                      On Stop
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      id: 'client-contact',
      accessorFn: (row) => getClientContacts(row, ref.clients),
      header: 'Client Contact',
      cell: ({ row }) => getClientContacts(row.original, ref.clients),
    },
    {
      id: 'account-manager',
      accessorFn: (row) => {
        if (!row.accountManagerId) return '-';
        return getManagerName(row.accountManagerId, ref.employees);
      },
      header: 'Account Manager',
      cell: ({ row }) => {
        if (!row.original.accountManagerId) return <span className="text-muted-foreground text-sm">-</span>;
        return <span>{getManagerName(row.original.accountManagerId, ref.employees)}</span>;
      },
    },
    {
      id: 'project-manager',
      accessorFn: (row) => getManagerName(row.projectManagerId, ref.employees),
      header: 'Project Manager',
      cell: ({ row }) =>
        getManagerName(row.original.projectManagerId, ref.employees),
    },
    {
      id: 'project-status',
      accessorFn: (row) => row.statusId,
      header: 'Project Status',
      cell: ({ row }) => {
        if (row.original.statusId == null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <Badge
            variant="outline"
            className={getProjectStatusColor(row.original.statusId)}
            disableHover
          >
            {statusLabels[row.original.statusId] || PROJECT_STATUS_LABELS[row.original.statusId] || formatStatus(String(row.original.statusId))}
          </Badge>
        );
      },
    },
    {
      id: 'health-indicator',
      accessorFn: (row) => row.health,
      header: 'Health Indicator',
      cell: ({ row }) => {
        const project = row.original;
        if (!project.health) return <span className="text-muted-foreground text-sm">-</span>;
        const thresholds = getBudgetHealthThresholds();
        const breakdown = deriveProjectHealthWithBreakdown(
          {
            actualCost: project.actualCosts ?? 0,
            budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
            startDate: project.startDate,
            endDate: project.endDate,
            completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
            actualHours: project.actualHours ?? 0,
            budgetHours: project.budgetHours ?? 0,
            wipAmount: project.wipAmount ?? 0,
            actualRevenue: project.actualRevenue ?? 0,
            budgetRevenue: project.budgetRevenue ?? 0,
            actualGrossMarginPct: null,      // Not loaded at list level
            realisationRatePct: project.realisationRatePct ?? null,
            forecastEndDate: project.forecastEndDate,
          },
          thresholds
        );
        // The badge uses the server-derived health (usp_Project_GetPaginated
        // computes the same multi-dimensional rollup, including realisation
        // rate) so the list matches the drill-down exactly. The breakdown below
        // is the tooltip detail, fed the server realisation rate so it agrees.
        const derivedHealth = project.health;
        const healthLabel = derivedHealth === 'green' ? 'Healthy' : derivedHealth === 'amber' ? 'At Risk' : 'Critical';
        const signalDot: Record<string, string> = { green: '🟢', amber: '🟡', red: '🔴' };
        return (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant={healthToVariant(derivedHealth)}
                  className="gap-1.5"
                  disableHover
                >
                  <StatusIndicator variant={derivedHealth === 'green' ? 'success' : derivedHealth === 'amber' ? 'warning' : 'error'} size="sm" />
                  {healthLabel}
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="p-0 w-64">
                <div className="px-3 py-2 border-b">
                  <p className="text-xs font-semibold text-foreground">Health Breakdown</p>
                  <p className="text-xs text-muted-foreground">Worst signal across all dimensions</p>
                </div>
                <div className="px-3 py-2 space-y-1.5">
                  {breakdown.dimensions.map((dim) => (
                    <div key={dim.label} className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs leading-none">{signalDot[dim.signal]}</span>
                        <span className="text-xs font-medium text-foreground truncate">{dim.label}</span>
                      </div>
                      <span className="text-xs text-muted-foreground text-right shrink-0">{dim.detail}</span>
                    </div>
                  ))}
                  {breakdown.dimensions.length === 0 && (
                    <p className="text-xs text-muted-foreground">No dimensions evaluated</p>
                  )}
                </div>
                <div className="px-3 py-1.5 border-t">
                  <p className="text-xs text-muted-foreground">Open project for milestones &amp; realisation</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      id: 'budget',
      accessorFn: (row) => row.budgetRevenue,
      header: 'Sales Budget',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.budgetRevenue)}
        </SensitiveData>
      ),
    },
    {
      id: 'actual-sales',
      accessorFn: (row) => row.actualRevenue,
      header: 'Actual Sales',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.actualRevenue)}
        </SensitiveData>
      ),
    },
    {
      id: 'sales-variance',
      accessorFn: (row) => row.salesVariancePercent,
      header: 'Sales Variance',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={getVarianceColor(-row.original.salesVariancePercent)}>
                  {formatCurrency(row.original.salesVariance)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {formatPercent(row.original.salesVariancePercent)} remaining
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </SensitiveData>
      ),
    },
    {
      id: 'cost-budget',
      accessorFn: (row) => row.budgetLabour + row.budgetExpense,
      header: 'Cost Budget',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.budgetLabour + row.original.budgetExpense)}
        </SensitiveData>
      ),
    },
    {
      id: 'actual-cost',
      accessorFn: (row) => row.actualCosts,
      header: 'Actual Costs',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.actualCosts)}
        </SensitiveData>
      ),
    },
    {
      id: 'cost-variance',
      accessorFn: (row) => row.costVariancePercent,
      header: 'Cost Variance',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={getVarianceColor(-row.original.costVariancePercent)}>
                  {formatCurrency(row.original.costVariance)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {formatPercent(row.original.costVariancePercent)} remaining
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </SensitiveData>
      ),
    },

    {
      id: 'wip-amount',
      accessorFn: (row) => row.wipAmount,
      header: 'WIP Amount',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          <span className={getWIPColor(row.original.wipAmount)}>
            {formatCurrency(row.original.wipAmount)}
          </span>
        </SensitiveData>
      ),
    },
    {
      id: 'gross-profit',
      accessorFn: (row) => row.actualSales - row.actualCosts,
      header: 'Gross Profit',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const gp = row.original.actualSales - row.original.actualCosts;
        return (
          <SensitiveData>
            <span
              className={
                gp >= 0 ? 'text-success-600' : 'text-error-600'
              }
            >
              {formatCurrency(gp)}
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'forecast-at-completion',
      accessorFn: (row) =>
        calculateForecastAtCompletion(
          row.actualCosts,
          0,
          row.budgetLabour,
          row.budgetExpense,
          row.completionPercent,
        ),
      header: 'Forecast at Completion',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const eac = calculateForecastAtCompletion(
          row.original.actualCosts,
          0,
          row.original.budgetLabour,
          row.original.budgetExpense,
          row.original.completionPercent,
        );
        return (
          <SensitiveData>{formatCurrency(eac)}</SensitiveData>
        );
      },
    },
    {
      id: 'allocated-hours',
      accessorFn: (row) => row.budgetHours,
      header: 'Resource Budget (hrs)',
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original.budgetHours == null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <SensitiveData>
            {row.original.budgetHours.toLocaleString()} hrs
          </SensitiveData>
        );
      },
    },
    {
      id: 'actual-hours',
      accessorFn: (row) => row.actualHours,
      header: 'Actual Hours',
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original.actualHours == null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <SensitiveData>
            {row.original.actualHours.toLocaleString()} hrs
          </SensitiveData>
        );
      },
    },
    {
      id: 'hours-variance',
      accessorFn: (row) => row.hoursVariancePercent,
      header: 'Hours Variance',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const v = row.original.hoursVariance;
        const vp = row.original.hoursVariancePercent;
        if (v == null || vp == null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <SensitiveData>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={getVarianceColor(vp)}>
                    {v > 0 ? '+' : ''}{v.toLocaleString()} hrs
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {vp > 0 ? '+' : ''}{formatPercent(vp)} remaining
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </SensitiveData>
        );
      },
    },

    {
      id: 'completion-percentage',
      accessorFn: (row) => computeEffectiveCompletionPercent(row),
      header: 'Completion Percentage',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const value = computeEffectiveCompletionPercent(row.original);
        return (
          <div className="flex items-center gap-2">
            <Progress
              value={value}
              className="h-2 w-20"
            />
            <span className="text-xs text-muted-foreground w-10 text-right">
              {formatPercent(value, 0)}
            </span>
          </div>
        );
      },
    },
    {
      id: 'margin',
      accessorFn: (row) => {
        const gpMethod = getFinanceSettings().gpCalculationMethod;
        const result = calculateGP(
          {
            revenue: row.actualSales,
            totalDirectCosts: row.actualCosts,
            labourCosts: row.actualLabourCosts ?? 0,
            subcontractorCosts: row.actualSubcontractorCosts ?? 0,
          },
          gpMethod
        );
        return result.grossMarginPct;
      },
      header: 'Gross Margin',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const gpMethod = getFinanceSettings().gpCalculationMethod;
        const result = calculateGP(
          {
            revenue: row.original.actualSales,
            totalDirectCosts: row.original.actualCosts,
            labourCosts: row.original.actualLabourCosts ?? 0,
            subcontractorCosts: row.original.actualSubcontractorCosts ?? 0,
          },
          gpMethod
        );
        const margin = result.grossMarginPct;
        return (
          <SensitiveData>
            <span className={margin >= 0 ? 'text-success-600' : 'text-error-600'}>
              {formatPercent(margin)}
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'project-type',
      accessorFn: (row) => row.type,
      header: 'Project Type',
      cell: ({ row }) => {
        if (!row.original.type) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <Badge
            variant="outline"
            className={getProjectTypeColor(row.original.type)}
            disableHover
          >
            {formatProjectType(row.original.type)}
          </Badge>
        );
      },
    },
    {
      id: 'phase',
      accessorFn: (row) => deriveProjectPhase(row.statusId, row.completionPercent),
      header: 'Phase/Stage',
      cell: ({ row }) => {
        const phase = deriveProjectPhase(row.original.statusId, row.original.completionPercent);
        return (
          <Badge
            variant="outline"
            className={PHASE_COLORS[phase] ?? ''}
            disableHover
          >
            {phase}
          </Badge>
        );
      },
    },
    {
      id: 'start-date',
      accessorFn: (row) => row.startDate,
      header: 'Start Date',
      cell: ({ row }) => formatDate(row.original.startDate),
    },
    {
      id: 'end-date',
      accessorFn: (row) => row.endDate,
      header: 'End Date',
      cell: ({ row }) => formatDate(row.original.endDate),
    },
    {
      id: 'days-remaining',
      accessorFn: (row) => calculateDaysRemaining(row.endDate),
      header: 'Days Remaining',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const days = calculateDaysRemaining(row.original.endDate);
        if (days === null) {
          return (
            <span className="text-muted-foreground text-sm">-</span>
          );
        }
        return (
          <span className={getDaysRemainingColor(days)}>
            {days > 0
              ? `${days} days`
              : days === 0
                ? 'Today'
                : 'Overdue'}
          </span>
        );
      },
    },
    {
      id: 'next-milestone',
      accessorFn: (row) => {
        const m = getNextMilestone(row.id, ref.milestones);
        return m?.name ?? '';
      },
      header: 'Next Milestone',
      cell: ({ row }) => {
        const milestone = getNextMilestone(
          row.original.id,
          ref.milestones,
        );
        if (!milestone) {
          return (
            <span className="text-muted-foreground text-sm">-</span>
          );
        }
        return (
          <div>
            <div className="font-medium text-sm">{milestone.name}</div>
            <div className="text-xs text-muted-foreground">
              {formatDate(milestone.dueDate)}
            </div>
          </div>
        );
      },
    },
    {
      id: 'team-members',
      accessorFn: (row) =>
        getAllocatedResources(row.id, ref.resourceAllocations),
      header: 'Team Members',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getAllocatedResources(
          row.original.id,
          ref.resourceAllocations,
        );
        return count > 0
          ? `${count} member${count !== 1 ? 's' : ''}`
          : '-';
      },
    },

    {
      id: 'tasks-overdue',
      accessorFn: (row) => getOverdueTasks(row.id, ref.tasks),
      header: 'Overdue Tasks',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getOverdueTasks(row.original.id, ref.tasks);
        return count > 0 ? (
          <Badge variant="destructive" disableHover>
            {count}
          </Badge>
        ) : (
          <span className="text-success-600 text-sm">0</span>
        );
      },
    },
    {
      id: 'overdue-milestones',
      accessorFn: (row) => getOverdueMilestones(row.id, ref.milestones),
      header: 'Overdue Milestones',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getOverdueMilestones(
          row.original.id,
          ref.milestones,
        );
        return count > 0 ? (
          <Badge variant="destructive" disableHover>
            {count}
          </Badge>
        ) : (
          <span className="text-success-600 text-sm">0</span>
        );
      },
    },
    {
      id: 'last-updated',
      accessorFn: (row) => row.updatedAt,
      header: 'Last Updated',
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span className="text-sm">
                {formatRelativeTime(row.original.updatedAt)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {formatDate(row.original.updatedAt)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      id: 'milestones-completed',
      accessorFn: (row) => getCompletedMilestones(row.id, ref.milestones),
      header: 'Milestones Completed',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const completed = getCompletedMilestones(
          row.original.id,
          ref.milestones,
        );
        const total = getTotalMilestones(
          row.original.id,
          ref.milestones,
        );
        return total > 0 ? (
          <span>
            {completed} / {total}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      },
    },
    {
      id: 'gross-margin',
      accessorFn: (row) => {
        const gpMethod = getFinanceSettings().gpCalculationMethod;
        const result = calculateGP(
          {
            revenue: row.actualSales,
            totalDirectCosts: row.actualCosts,
            labourCosts: row.actualLabourCosts ?? 0,
            subcontractorCosts: row.actualSubcontractorCosts ?? 0,
          },
          gpMethod
        );
        return result.grossMarginPct;
      },
      header: 'Gross Margin',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const gpMethod = getFinanceSettings().gpCalculationMethod;
        const result = calculateGP(
          {
            revenue: row.original.actualSales,
            totalDirectCosts: row.original.actualCosts,
            labourCosts: row.original.actualLabourCosts ?? 0,
            subcontractorCosts: row.original.actualSubcontractorCosts ?? 0,
          },
          gpMethod
        );
        const margin = result.grossMarginPct;
        return (
          <SensitiveData>
            <span className={margin >= 0 ? 'text-success-600' : 'text-error-600'}>
              {formatPercent(margin)}
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'billing-status',
      accessorFn: (row) => deriveBillingStatus(row),
      header: 'Billing Status',
      cell: ({ row }) => {
        const status = deriveBillingStatus(row.original);
        return (
          <Badge
            variant="outline"
            className={BILLING_STATUS_COLORS[status] ?? ''}
            disableHover
          >
            {status}
          </Badge>
        );
      },
    },
    {
      id: 'billable',
      accessorFn: (row) => row.billable ?? true,
      header: 'Billable',
      cell: ({ row }) => {
        const isBillable = row.original.billable ?? true;
        return isBillable ? (
          <Badge
            variant="outline"
            className="text-success-700 bg-success-50 border-success-200"
            disableHover
          >
            Billable
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-muted-foreground bg-muted/40"
            disableHover
          >
            Non-billable
          </Badge>
        );
      },
    },
    {
      id: 'utilisation-rate',
      accessorFn: (row) => getUtilisationRate(row),
      header: 'Utilisation Rate',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const rate = getUtilisationRate(row.original);
        return (
          <span
            className={
              rate > 100
                ? 'text-error-600'
                : rate > 80
                  ? 'text-warning-600'
                  : 'text-success-600'
            }
          >
            {formatPercent(rate)}
          </span>
        );
      },
    },
    {
      id: 'duration',
      accessorFn: (row) =>
        calculateDuration(row.startDate, row.endDate),
      header: 'Duration',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const d = calculateDuration(
          row.original.startDate,
          row.original.endDate,
        );
        return `${d} day${d !== 1 ? 's' : ''}`;
      },
    },
    {
      id: 'risk-level',
      accessorFn: (row) => deriveRiskLevel(row),
      header: 'Risk Level',
      cell: ({ row }) => {
        const level = deriveRiskLevel(row.original);
        return (
          <Badge
            variant="outline"
            className={RISK_LEVEL_COLORS[level] ?? ''}
            disableHover
          >
            {level}
          </Badge>
        );
      },
    },
    {
      id: 'quality-score',
      accessorFn: (row) => deriveQualityScore(row),
      header: 'Quality Score',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const score = deriveQualityScore(row.original);
        if (score === null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <span className={`font-medium ${getQualityScoreColor(score)}`}>
            {score}/100
          </span>
        );
      },
    },
  ];
}

// ── Component ────────────────────────────────────────────────────────────

export function ProjectsGrid({
  data,
  referenceData,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  onRowClick,
  loading = false,
  isTransitioning = false,
  pageSize = 25,
  hasActiveFilters = false,
  pagination,
  onPageChange,
  onPageSizeChange,
  hideHeader = false,
  subtotalRow,
  selectedIds,
  onSelectionChange,
}: ProjectsGridProps) {
  // Get dynamic status labels from context
  const { statusLabels } = useProjectStatuses();

  // ── Selection helpers ──────────────────────────────────────────────
  const selectionEnabled = onSelectionChange !== undefined;

  const isAllPageSelected = useMemo(() => {
    if (!selectionEnabled || data.length === 0) return false;
    return data.every((p) => selectedIds?.has(p.id));
  }, [selectionEnabled, data, selectedIds]);

  const isSomePageSelected = useMemo(() => {
    if (!selectionEnabled) return false;
    return data.some((p) => selectedIds?.has(p.id));
  }, [selectionEnabled, data, selectedIds]);

  const handleToggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    if (isAllPageSelected) {
      data.forEach((p) => next.delete(p.id));
    } else {
      data.forEach((p) => next.add(p.id));
    }
    onSelectionChange(next);
  }, [onSelectionChange, selectedIds, data, isAllPageSelected]);

  const handleToggleRow = useCallback(
    (projectId: number, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onSelectionChange) return;
      const next = new Set(selectedIds);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      onSelectionChange(next);
    },
    [onSelectionChange, selectedIds],
  );

  // Build full column set with cell renderers (memoised on reference data)
  const allColumns = useMemo(
    () => buildProjectColumns(referenceData, statusLabels),
    [referenceData, statusLabels],
  );

  // Selection checkbox column prepended when selection is enabled
  const checkboxColumn: ColumnDef<Project, unknown> = useMemo(
    () => ({
      id: '__select__',
      enableSorting: false,
      meta: { width: 48 },
      header: () => (
        <Checkbox
          checked={isAllPageSelected ? true : isSomePageSelected ? 'indeterminate' : false}
          onCheckedChange={handleToggleAll}
          aria-label="Select all on page"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedIds?.has(row.original.id) ?? false}
          onCheckedChange={() => {/* handled by onClick below */}}
          aria-label={`Select ${row.original.name}`}
          onClick={(e) => handleToggleRow(row.original.id, e)}
        />
      ),
    }),
    [isAllPageSelected, isSomePageSelected, handleToggleAll, handleToggleRow, selectedIds],
  );

  // Filter and order columns to match the visible ColumnDefinitions
  const visibleColumnDefs = useMemo(() => {
    const visibleDefs = columnDefinitions
      .filter((cd) => cd.visible)
      .sort((a, b) => a.order - b.order);

    const columnMap = new Map(allColumns.map((c) => [c.id, c]));
    const dataColumns = visibleDefs
      .map((cd) => columnMap.get(cd.id))
      .filter((c): c is ColumnDef<Project, unknown> => c !== undefined);

    return selectionEnabled ? [checkboxColumn, ...dataColumns] : dataColumns;
  }, [columnDefinitions, allColumns, selectionEnabled, checkboxColumn]);

  // Build TanStack visibility state from ColumnDefinitions
  const columnVisibility = useMemo(
    () => columnDefinitionsToVisibility(columnDefinitions),
    [columnDefinitions],
  );

  // Empty state messages
  const emptyMessage = hasActiveFilters ? (
    <div className="space-y-2">
      <p className="text-lg font-medium">No projects found</p>
      <p className="text-sm">Try adjusting your filters or search term</p>
    </div>
  ) : (
    <div className="space-y-2">
      <p className="text-lg font-medium">No projects yet</p>
      <p className="text-sm">Get started by creating your first project</p>
    </div>
  );

  // Pagination content
  const paginationContent =
    !loading && pagination && pagination.totalItems > 0 && onPageChange ? (
      <PaginationControls
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showPageSizeSelector={true}
        className="w-full"
      />
    ) : null;

  return (
    <DataTableEnhanced<Project, unknown>
      columns={visibleColumnDefs}
      data={data}
      columnVisibility={columnVisibility}
      columnSorts={columnSorts}
      onColumnSortChange={onColumnSortChange}
      loading={loading}
      loadingRowCount={pageSize}
      isTransitioning={isTransitioning}
      emptyMessage={emptyMessage}
      onRowClick={onRowClick}
      getRowId={(row) => String(row.id)}
      dataTour="project-list-table"
      paginationContent={paginationContent}
      hideHeader={hideHeader}
      appendedRows={subtotalRow}
      className="rounded-none border-0 border-t"
    />
  );
}
